import { FileText, GraduationCap, Home, Link as LinkIcon, LogOut, Menu, School, Search, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { DebugInfoPanel } from '../components/DebugInfoPanel';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export const DashboardLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<{
    name: string;
    email: string;
    id: string;
  } | null>(null);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        setUser(JSON.parse(userStr));
      }
    } catch (e) {
      console.error('Failed to parse user', e);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('currentUser');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* Top Navigation Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
          {/* Logo / Brand */}
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="bg-blue-600 p-1.5 rounded-lg">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-blue-900 hidden sm:block">Admission Entry</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="/dashboard"
              className="text-sm font-medium text-gray-600 hover:text-blue-600 flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              {t('menu.dashboard')}
            </Link>
            <Link
              to="/application/new"
              className="text-sm font-medium text-gray-600 hover:text-blue-600 flex items-center gap-2"
            >
              <Search className="h-4 w-4" />
              {t('menu.search')}
            </Link>
            {/* Preparation Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-sm font-medium text-gray-600 hover:text-blue-600 flex items-center gap-2 h-auto p-0 hover:bg-transparent"
                >
                  <FileText className="h-4 w-4" />
                  {t('menu.preparation')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" /> {t('menu.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/highschool')}>
                  <School className="mr-2 h-4 w-4" /> {t('menu.highSchool')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/api')}>
                  <LinkIcon className="mr-2 h-4 w-4" /> {t('menu.apiLink')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* User Profile & Mobile Menu */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full border border-gray-200 hover:bg-gray-100 p-0"
                  >
                    <div className="h-full w-full rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                      <p className="text-[10px] text-gray-400 mt-1">ID: {user.id}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>{t('menu.profile')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/documents')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>{t('menu.documents')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('menu.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
            )}

            {/* Mobile Menu Trigger (Placeholder for now) */}
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
          <p>&copy; 2026 Admission Platform. All rights reserved.</p>
          <div className="flex gap-4 mt-2 md:mt-0">
            <Link to="#" className="hover:text-gray-900">
              Privacy Policy
            </Link>
            <Link to="#" className="hover:text-gray-900">
              Terms of Service
            </Link>
            <Link to="#" className="hover:text-gray-900">
              Contact Support
            </Link>
          </div>
        </div>
      </footer>
      <DebugInfoPanel />
    </div>
  );
};
